import React, { Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  customerRoutes,
  outOfNavRoutes,
  routes
} from '../utils/constants/routes';
import Login from './login';
import Home from './home/Home';
import Exception from '../components/Exceptions/Exception';
import useAuthContext from '../contexts/AuthContext';
import BasicLayout from '../components/layouts/BasicLayout';
import ProfileRouter from './profile/ProfileRouter';
import Confirmation from './emailConfirmation/Confirmation';
import QuotesRouter from './quotes/QuotesRouter';
import SettingsRouter from './settings/SettingsRouter';
import ContractsRouter from './contracts/ContractsRouter';
import DocumentsRouter from './documents/DocumentsRouter';
import TracfinRouter from './tracfin/TracfinRouter';
import ContactsRouter from './contacts/ContactsRouter';
import MissingPartsRouter from './missingparts/MissingPartsRouter';
import UnpaidRouter from './unpaid/UnpaidRouter';
import { AccountingRouter } from './accounting/AccountingRouter';
import ListComplaints from './complaints/ListComplaints';
import { MyAccountant } from './myAccountant/MyAccountant';
import { DocumentTitle } from '../components/DocumentTitle/DocumentTitle';

const PrivateRouteWrapper = ({ children, hideToCustomer, onlyShowToAcc }) => {
  const location = useLocation();
  const { isValid, user } = useAuthContext();
  const splitPath = location.pathname.split('/');

  const path = `/${
    splitPath[2] && !['show', 'edit', 'create'].includes(splitPath[2])
      ? splitPath[2]
      : splitPath[1]
  }`;

  const isCustomer = user?.role === 'guests:GUEST';
  const isACC = user?.role === 'users:SUPER-USER';
  const show =
    (!isCustomer || (isCustomer && !hideToCustomer)) &&
    (!onlyShowToAcc || (onlyShowToAcc && isACC));

  if (!isValid) {
    return (
      <Navigate to={outOfNavRoutes.LOGIN} state={{ from: location }} replace />
    );
  }

  if (!show) {
    return (
      <BasicLayout path={path}>
        <Exception />
      </BasicLayout>
    );
  }

  return (
    <BasicLayout path={path}>
      <Suspense fallback={<div>Chargement...</div>}>{children}</Suspense>
    </BasicLayout>
  );
};

PrivateRouteWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  onlyShowToAcc: PropTypes.bool,
  hideToCustomer: PropTypes.bool
};

PrivateRouteWrapper.defaultProps = {
  hideToCustomer: false,
  onlyShowToAcc: false
};

const RedirectExternal = ({ url }) => {
  React.useEffect(() => {
    window.location.href = url;
  }, [url]);
  return null;
};

RedirectExternal.propTypes = {
  url: PropTypes.string.isRequired
};

const Router = () => {
  return (
    <BrowserRouter>
      <DocumentTitle />
      <Suspense fallback={<div>Chargement...</div>}>
        <Routes>
          <Route path={`${outOfNavRoutes.LOGIN}/:token`} element={<Login />} />
          <Route path={outOfNavRoutes.LOGIN} element={<Login />} />
          <Route
            path={`${outOfNavRoutes.CONFIRMATION}/:token`}
            element={<Confirmation />}
          />

          <Route
            path={`${outOfNavRoutes.PROFILE}/*`}
            element={
              <PrivateRouteWrapper>
                <ProfileRouter />
              </PrivateRouteWrapper>
            }
          />
          <Route
            path={`${outOfNavRoutes.SETTINGS}/*`}
            element={
              <PrivateRouteWrapper onlyShowToAcc>
                <SettingsRouter />
              </PrivateRouteWrapper>
            }
          />
          <Route
            path={routes.HOME}
            element={
              <PrivateRouteWrapper>
                <Home />
              </PrivateRouteWrapper>
            }
          />
          <Route
            path={`${routes.CONTRACTS}/*`}
            element={
              <PrivateRouteWrapper>
                <ContractsRouter />
              </PrivateRouteWrapper>
            }
          />
          <Route
            path={`${routes.DOCUMENTS}/*`}
            element={
              <PrivateRouteWrapper>
                <DocumentsRouter />
              </PrivateRouteWrapper>
            }
          />
          <Route
            path={`${routes.CONTACTS}/*`}
            element={
              <PrivateRouteWrapper>
                <ContactsRouter />
              </PrivateRouteWrapper>
            }
          />
          <Route
            path={`${routes.ACCOUNTING}/*`}
            element={
              <PrivateRouteWrapper>
                <AccountingRouter />
              </PrivateRouteWrapper>
            }
          />

          <Route
            path={routes.CLAIMS}
            element={
              <RedirectExternal url="https://www.groupe-assurmax.fr/espace-sinistre/" />
            }
          />

          <Route
            path={`${customerRoutes.COMPLAINTS}/*`}
            element={
              <PrivateRouteWrapper>
                <ListComplaints />
              </PrivateRouteWrapper>
            }
          />
          <Route
            path={`${customerRoutes.ACCOUNTANT}/*`}
            element={
              <PrivateRouteWrapper>
                <MyAccountant />
              </PrivateRouteWrapper>
            }
          />
          <Route
            path={`${routes.MISSING_PARTS}/*`}
            element={
              <PrivateRouteWrapper>
                <MissingPartsRouter />
              </PrivateRouteWrapper>
            }
          />
          <Route
            path={`${routes.UNPAID_SUSPENDED}/*`}
            element={
              <PrivateRouteWrapper>
                <UnpaidRouter />
              </PrivateRouteWrapper>
            }
          />
          <Route
            path={`${routes.QUOTES}/*`}
            element={
              <PrivateRouteWrapper hideToCustomer>
                <QuotesRouter />
              </PrivateRouteWrapper>
            }
          />
          <Route
            path={`${routes.TRACFIN}/*`}
            element={
              <PrivateRouteWrapper hideToCustomer>
                <TracfinRouter />
              </PrivateRouteWrapper>
            }
          />
          <Route
            path="*"
            element={
              <PrivateRouteWrapper>
                <Exception />
              </PrivateRouteWrapper>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default Router;
